import { ArrowLeft, ArrowRight, Check, Circle, Loader2, Sparkles, Square } from "lucide-react";
import { Button } from "@/modern/components/ui/button-shadcn";
import { cn } from "@/modern/utils";
import { useContextForm } from "../../context/ContextFormProvider";

interface AnswerCardProps {
  label: string;
  isSelected: boolean;
  isMulti: boolean;
  disabled: boolean;
  onToggle: () => void;
}

const AnswerCard = ({ label, isSelected, isMulti, disabled, onToggle }: AnswerCardProps) => (
  <button
    type="button"
    onClick={onToggle}
    disabled={disabled}
    aria-pressed={isSelected}
    className={cn(
      "flex w-full items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition-all duration-150",
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
      disabled && "opacity-50 cursor-not-allowed",
      !disabled && "cursor-pointer",
      isSelected
        ? "border-primary/50 bg-primary/10"
        : "border-border/60 bg-muted/20 hover:border-border hover:bg-muted/40"
    )}
  >
    <div
      className={cn(
        "flex h-7 w-7 shrink-0 items-center justify-center rounded-lg transition-colors",
        isSelected ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
      )}
      aria-hidden
    >
      {isSelected ? (
        <Check className="h-3.5 w-3.5" strokeWidth={2.5} />
      ) : isMulti ? (
        <Square className="h-3 w-3" />
      ) : (
        <Circle className="h-3 w-3" />
      )}
    </div>

    <span className="flex-1 min-w-0 text-sm font-medium text-foreground leading-snug">
      {label}
    </span>
  </button>
);

interface QuestionSectionProps {
  index: number;
  total: number;
  text: string;
  isMulti: boolean;
  options: string[];
  currentAnswers: string[];
  hasOpenAIKey: boolean;
  onToggleOption: (option: string) => void;
  isLast: boolean;
}

const QuestionSection = ({
  index,
  total,
  text,
  isMulti,
  options,
  currentAnswers,
  hasOpenAIKey,
  onToggleOption,
  isLast,
}: QuestionSectionProps) => {
  const hasAnswer = currentAnswers.length > 0;

  return (
    <section className={cn("space-y-3", !isLast && "pb-5 mb-5 border-b border-border/30")}>
      <div>
        <p className="text-xs font-medium text-muted-foreground">
          Question {index + 1} of {total}
          <span className="text-muted-foreground/50"> · </span>
          {isMulti ? "Select all that apply" : "Choose one"}
          {hasAnswer && (
            <span className="text-primary"> · Answered</span>
          )}
        </p>
        <h3 className="mt-1.5 text-base font-semibold text-foreground leading-relaxed">
          {text}
        </h3>
      </div>

      <div className="space-y-2">
        {options.map((option) => (
          <AnswerCard
            key={option}
            label={option}
            isSelected={currentAnswers.includes(option)}
            isMulti={isMulti}
            disabled={!hasOpenAIKey}
            onToggle={() => onToggleOption(option)}
          />
        ))}
      </div>
    </section>
  );
};

export const QuestionsStep = () => {
  const {
    draftQuestions,
    answers,
    setAnswer,
    handleBuildContext,
    isSubmittingAnswers,
    handleBackFromQuestions,
    hasOpenAIKey,
  } = useContextForm();

  const total = draftQuestions.length;
  const answeredCount = Object.values(answers).filter((a) => a.length > 0).length;
  const canBuild = hasOpenAIKey && answeredCount > 0 && !isSubmittingAnswers;

  return (
    <div className="flex flex-col h-full min-h-0 overflow-hidden">
      <div className="shrink-0 px-8 py-3.5 border-b border-border/40">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-foreground">A few quick questions</h2>
            <p className="mt-0.5 text-sm text-muted-foreground">
              Help us tailor your Context — skip anything you&apos;re not sure about.
            </p>
          </div>
          {total > 0 && (
            <span className="shrink-0 text-xs font-medium text-muted-foreground tabular-nums pt-0.5">
              {answeredCount} of {total} answered
            </span>
          )}
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-8 py-4">
        {draftQuestions.map((q, index) => {
          const currentAnswers = answers[index] ?? [];
          const isMulti = q.question_type === "multi_select";

          const toggleOption = (option: string) => {
            if (!hasOpenAIKey) return;
            if (isMulti) {
              if (currentAnswers.includes(option)) {
                setAnswer(index, currentAnswers.filter((a) => a !== option));
              } else {
                setAnswer(index, [...currentAnswers, option]);
              }
            } else {
              setAnswer(index, currentAnswers[0] === option ? [] : [option]);
            }
          };

          return (
            <QuestionSection
              key={index}
              index={index}
              total={total}
              text={q.text}
              isMulti={isMulti}
              options={q.options}
              currentAnswers={currentAnswers}
              hasOpenAIKey={hasOpenAIKey}
              onToggleOption={toggleOption}
              isLast={index === total - 1}
            />
          );
        })}
      </div>

      <div className="shrink-0 px-8 py-3 border-t border-border/40 flex items-center justify-between gap-4">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleBackFromQuestions}
          disabled={isSubmittingAnswers || !hasOpenAIKey}
          className="gap-1.5 text-muted-foreground"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back
        </Button>

        <div className="flex flex-wrap items-center justify-end gap-2">
          {!canBuild && !isSubmittingAnswers && (
            <p className="text-xs text-muted-foreground hidden sm:block">
              Answer at least one, or continue without.
            </p>
          )}
          <button
            type="button"
            onClick={() => handleBuildContext(true)}
            className="text-xs font-medium text-muted-foreground hover:text-foreground hover:underline disabled:opacity-50 disabled:cursor-not-allowed disabled:no-underline"
            disabled={isSubmittingAnswers || !hasOpenAIKey}
          >
            Continue without answers
          </button>
          <Button
            type="button"
            onClick={() => handleBuildContext()}
            disabled={!canBuild}
            className="gap-2"
          >
            {isSubmittingAnswers ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Generating context…
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4" />
                Build Context
                <ArrowRight className="h-4 w-4" />
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};
